import React, { useEffect, useState, useRef, useCallback } from "react";
import { Icon, TooltipComponent } from "../../components/Component";
import {
  Badge,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
  Alert,
  Button,
} from "reactstrap";
import SimpleBar from "simplebar-react";
import miscService from "../../api/misc/misc";
import dayjs from "dayjs";
import { Flex, message, Spin } from "antd";
import InboxMessages from "./InboxMessages";
import Swal from "sweetalert2";
import debounce from "lodash.debounce";

const moduleColors: Record<string, string> = {
  Issues: "success",
  TimeLogs: "warning",
  SubTasks: "danger",
  Tasks: "info",
  Projects: "pink",
  Default: "light",
};

const MODULE_IDS = {
  PROJECT: "82",
  PHASE: "83",
  ACTIVITY: "84",
  TASK: "85",
  SUB_TASK: "111",
  ISSUE: "86",
  TIME_LOG: "156",
};

const NotificationList: React.FC<any> = ({ currentTab, setCounts }) => {
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [refresh, setRefresh] = useState(false);
  const [tabData, setTabData] = useState<any>([]);
  const [searchText, setSearchText] = useState("");
  const [searchToggle, setSearchToggle] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 9,
    direction: "P",
    paginationId: null,
    pageDiff: 0,
    moduleId: undefined as string | undefined,
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [mailId, setMailId] = useState(1);
  const [messageView, setMessageView] = useState(false);
  const [filter, setFilter] = useState("All");

  // Add refs to track API calls
  const isInitialMount = useRef(true);
  const apiCallInProgress = useRef(false);
  const searchTextRef = useRef("");

  const allSelected =
    tabData.length > 0 && selectedItems.size === tabData.length;

  const deleteNotifications = async (notificationIds: number[]) => {
    if (notificationIds.length === 0) {
      message.warning("Please select at least one notification to delete");
      return;
    }

    try {
      const { response, status } = await miscService.deleteNotifications(
        Array.from(notificationIds)
      );
      if (status) {
        Swal.fire({
          title: "Deleted!",
          text: `Successfully deleted ${notificationIds.length} notification(s)`,
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });

        // Reset to page 1 after delete success
        setPagination((prev) => ({
          ...prev,
          currentPage: 1,
          direction: "P",
          paginationId: null,
          pageDiff: 0,
        }));

        notificationCounts();

        setSelectedItems(new Set());
      } else {
        message.error("Failed to delete notification(s)");
      }
    } catch (err) {
      console.error("Error deleting notifications:", err);
      message.error("Error deleting notification(s)");
    }
  };

  // Delete selected notifications
  const handleDeleteSelected = () => {
    if (selectedItems.size === 0) {
      message.warning("Please select at least one notification to delete");
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: `You are about to delete ${selectedItems.size} notification(s). This action cannot be undone!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete them!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteNotifications(Array.from(selectedItems));
      }
    });
  };

  // Delete single notification
  const handleDeleteSingle = (notificationId: number) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        deleteNotifications([notificationId]);
      }
    });
  };

  const handleFilterChange = (moduleName: string, moduleId: any) => {
    setFilter(moduleName);
    setPagination((prev) => ({
      ...prev,
      currentPage: 1,
      pageSize: 9,
      direction: "P",
      paginationId: null,
      pageDiff: 0,
      moduleId: moduleId,
    }));
  };

  useEffect(() => {
    notificationCounts();
  }, []);

  const notificationCounts = async () => {
    try {
      const { response, status } = await miscService.notificationCount({
        appType: "PMS",
      });
      if (status) {
        setCounts({
          fyiCount: response.fyiCount,
          fyaCount: response.fyaCount,
        });
      }
    } catch (err) {
      console.log("err", err);
    }
  };

  useEffect(() => {
    setPagination({
      currentPage: 1,
      pageSize: 9,
      direction: "P",
      paginationId: null,
      pageDiff: 0,
      moduleId: undefined,
    });

    setSelectedItems(new Set());
    setFilter("All");
    setSearchText("");
    searchTextRef.current = "";
  }, [currentTab]);

  // useEffect(() => {
  //   fetchNotificationList(pagination, currentTab);
  // }, [pagination, refresh]);

  useEffect(() => {
    setMessageView(false);
  }, [currentTab]);

  const fetchNotificationList = useCallback(async (paginationParams: any, tab: any) => {
    if (apiCallInProgress.current) {
      // console.log('API call already in progress, skipping...');
      return;
    }

    setLoading(true);
    apiCallInProgress.current = true;

    const params: any = {
      ...paginationParams,
      sortOrder: "DESC",
      actionRequired: tab === "For Your Information" ? 0 : 1,
      appType: "PMS",
    };
    // Only add searchQuery param if search text is not empty
    if (searchTextRef.current && searchTextRef.current.trim()) {
      params.searchQuery = searchTextRef.current.trim();
    }
    try {
      const { response, status } = await miscService.notificationList(params);
      if (status) {
        const { list, count } = response;
        setTabData(list);
        setTotalItems(count);
        // setSelectedItems(new Set());
      }
    } catch (err) {
      console.log("err", err);
    } finally {
      setLoading(false);
      apiCallInProgress.current = false;
    }
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      fetchNotificationList(pagination, currentTab);
    }
  }, [pagination, refresh, currentTab, fetchNotificationList]);

  const handleIndividualCheckbox = (
    notificationId: number,
    checked: boolean
  ) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);
      if (checked) {
        newSelected.add(notificationId);
      } else {
        newSelected.delete(notificationId);
      }
      return newSelected;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = tabData.map((item: any) => item.notificationId);
      setSelectedItems(new Set(allIds));
    } else {
      setSelectedItems(new Set());
    }
  };

  const toggleRefresh = () => {
    // setRefresh(true);
    // setTimeout(() => setRefresh(false), 3000);
    setRefresh(prev => !prev);
  };

  // Debounced search handler - only calls API after user stops typing for 500ms
  const debouncedSearch = useRef(
    debounce((searchValue: string) => {
      searchTextRef.current = searchValue;
      setPagination((prev) => ({
        ...prev,
        currentPage: 1,
        direction: "P",
        paginationId: null,
        pageDiff: 0,
      }));
    }, 500)
  ).current;

  const onSearchChange = (e: any) => {
    let value = e.target.value;
    setSearchText(value);
    debouncedSearch(value);
  };

  const toggleSearch = () => {
    setSearchToggle(!searchToggle);

    if (searchToggle) {
      setSearchText("");
      searchTextRef.current = "";
      // Reset pagination to first page when search is closed
      setPagination((prev) => ({
        ...prev,
        currentPage: 1,
        direction: "P",
        paginationId: null,
        pageDiff: 0,
      }));
    } else {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  };

  const handlePageChange = (newPage: number) => {
    const isForward = newPage > pagination.currentPage;
    const direction = isForward ? "F" : "B";
    const pageDiff = Math.abs(newPage - pagination.currentPage) - 1;
    const paginationId = isForward
      ? tabData[tabData.length - 1]?.paginationId
      : tabData[0]?.paginationId;

    setPagination({
      ...pagination,
      currentPage: newPage,
      direction,
      pageDiff,
      paginationId,
    });
  };

  const nextPage = () => {
    if (pagination.currentPage < Math.ceil(totalItems / pagination.pageSize)) {
      handlePageChange(pagination.currentPage + 1);
    }
  };

  const prevPage = () => {
    if (pagination.currentPage > 1) {
      handlePageChange(pagination.currentPage - 1);
    }
  };

  const onListClick = async (ev: any, item: any) => {
    // console.log("item", item);
    ev.preventDefault();
    toggleMessageView();
    setMailId(item.notificationId);
    notificationCounts();
    setTabData((prevTabData: any) =>
      prevTabData.map((i: any) =>
        i.notificationId === item.notificationId ? { ...i, viewed: true } : i
      )
    );

    try {
      await messageRead(item.notificationId);
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const messageRead = async (id: any) => {
    try {
      const { response, status } = await miscService.notificationRead(id);
      if (status) {
      }
    } catch (err) {
      console.log("err", err);
    }
  };

  const toggleMessageView = () => {
    setMessageView(!messageView);
  };

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return (
    <div className="nk-ibx-body bg-white">
      {messageView ? (
        <InboxMessages
          mailId={mailId}
          toggleMessageView={toggleMessageView}
          onDeleteNotification={handleDeleteSingle}
        />
      ) : (
        <>
          {loading ? (
            <Flex align="center" gap="middle" className="pageLoader">
              <Spin size="large" className="m-auto" />
            </Flex>
          ) : (
            <React.Fragment>
              <div
                className="nk-ibx-head notify-head"
                style={{ minHeight: "75px" }}
              >
                <div className="nk-ibx-head-actions">
                  <div className="nk-ibx-head-check">
                    <div className="custom-control custom-control-sm custom-checkbox">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        className="custom-control-input nk-dt-item-check"
                        id="conversionAll"
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                      <label
                        className="custom-control-label"
                        htmlFor="conversionAll"
                      ></label>
                    </div>
                  </div>
                  <ul className="nk-ibx-head-tools g-1">
                    <li>
                      <a
                        href="#item"
                        onClick={(ev) => {
                          ev.preventDefault();
                          toggleRefresh();
                        }}
                        className="btn btn-icon btn-trigger"
                      >
                        <Icon name="undo"></Icon>
                      </a>
                    </li>

                    <li className="d-none d-sm-block">
                      <a
                        href="#item"
                        onClick={(ev) => {
                          ev.preventDefault();
                          handleDeleteSelected();
                        }}
                        className={`btn btn-icon btn-trigger`}
                      >
                        <Icon name="trash"></Icon>
                      </a>
                    </li>
                  </ul>
                </div>
                <div>
                  <ul className="nk-ibx-head-tools g-1">
                    <li>
                      <a
                        href="#prev"
                        onClick={(ev) => {
                          ev.preventDefault();
                          prevPage();
                        }}
                        className={`btn btn-trigger btn-icon btn-tooltip ${pagination.currentPage === 1 ? "disabled" : ""
                          }`}
                      >
                        <Icon name="arrow-left"></Icon>
                      </a>
                    </li>
                    <li>
                      <a
                        href="#next"
                        onClick={(ev) => {
                          ev.preventDefault();
                          nextPage();
                        }}
                        className={`btn btn-trigger btn-icon btn-tooltip ${pagination.currentPage ===
                          Math.ceil(totalItems / pagination.pageSize)
                          ? "disabled"
                          : ""
                          }`}
                      >
                        <Icon name="arrow-right"></Icon>
                      </a>
                    </li>
                    <li>
                      <a
                        href="#item"
                        onClick={(ev) => {
                          ev.preventDefault();
                          toggleSearch();
                        }}
                        className="btn btn-trigger btn-icon search-toggle toggle-search"
                      >
                        <Icon name="search"></Icon>
                      </a>
                    </li>
                    <li className="me-n1">
                      <UncontrolledDropdown>
                        <DropdownToggle
                          tag="a"
                          href="#item"
                          onClick={(ev) => ev.preventDefault()}
                          className="btn btn-trigger btn-icon toggle"
                        >
                          <Icon name="filter" />
                        </DropdownToggle>
                        <DropdownMenu end>
                          <DropdownItem
                            onClick={() => handleFilterChange("All", undefined)}
                            className={filter === "All" ? "active" : ""}
                          >
                            All
                          </DropdownItem>
                          <DropdownItem
                            onClick={() =>
                              handleFilterChange("Project", MODULE_IDS.PROJECT)
                            }
                            className={filter === "Project" ? "active" : ""}
                          >
                            Project
                          </DropdownItem>
                          <DropdownItem
                            onClick={() =>
                              handleFilterChange("Task", MODULE_IDS.TASK)
                            }
                            className={filter === "Task" ? "active" : ""}
                          >
                            Task
                          </DropdownItem>
                          <DropdownItem
                            onClick={() =>
                              handleFilterChange("SubTask", MODULE_IDS.SUB_TASK)
                            }
                            className={filter === "SubTask" ? "active" : ""}
                          >
                            SubTask
                          </DropdownItem>
                          <DropdownItem
                            onClick={() =>
                              handleFilterChange("Issue", MODULE_IDS.ISSUE)
                            }
                            className={filter === "Issue" ? "active" : ""}
                          >
                            Issue
                          </DropdownItem>
                          <DropdownItem
                            onClick={() =>
                              handleFilterChange("TimeLog", MODULE_IDS.TIME_LOG)
                            }
                            className={filter === "TimeLog" ? "active" : ""}
                          >
                            TimeLog
                          </DropdownItem>
                        </DropdownMenu>
                      </UncontrolledDropdown>
                    </li>
                  </ul>
                </div>
                <div className={`search-wrap ${searchToggle ? "active" : ""}`}>
                  <div className="search-content">
                    <a
                      href="#item"
                      onClick={(ev) => {
                        ev.preventDefault();
                        toggleSearch();
                      }}
                      className="search-back btn btn-icon toggle-search"
                    >
                      <Icon name="arrow-left"></Icon>
                    </a>
                    <input
                      type="text"
                      className="form-control border-transparent form-focus-none"
                      placeholder="Search"
                      value={searchText}
                      onChange={(e) => onSearchChange(e)}
                      ref={searchInputRef}
                      autoFocus={searchToggle}
                    />
                    <button className="search-submit btn btn-icon">
                      <Icon name="search"></Icon>
                    </button>
                  </div>
                </div>
              </div>

              {/* Selected items info */}
              {selectedItems.size > 0 && (
                <Alert color="info" className="m-3 py-2">
                  <div className="d-flex justify-content-between align-items-center">
                    <span>{selectedItems.size} notification(s) selected</span>
                  </div>
                </Alert>
              )}

              <React.Fragment>
                <SimpleBar className="nk-ibx-list">
                  {tabData.length > 0 ? (
                    tabData.map((item: any) => {
                      const isSelected = selectedItems.has(item.notificationId);
                      return (
                        <div
                          key={item.notificationId}
                          className={`nk-ibx-item ${item.viewed ? "" : "is-unread"
                            }`}
                        >
                          <a
                            href="#item"
                            onClick={(ev) => {
                              onListClick(ev, item);
                            }}
                            className="nk-ibx-link current"
                          >
                            <span></span>
                          </a>
                          <div className="nk-ibx-item-elem nk-ibx-item-check">
                            <div className="custom-control custom-control-sm custom-checkbox">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                className="custom-control-input nk-dt-item-check"
                                id={`${item.notificationId}`}
                                key={Math.random()}
                                onChange={(e) =>
                                  handleIndividualCheckbox(
                                    item.notificationId,
                                    e.target.checked
                                  )
                                }
                              />
                              <label
                                className="custom-control-label"
                                htmlFor={`${item.notificationId}`}
                              ></label>
                            </div>
                          </div>
                          <div
                            className="nk-ibx-item-elem nk-ibx-item"
                            style={{ background: "none", border: "none" }}
                          >
                            <div className="sub-text">{item.senderName}</div>
                          </div>
                          <div
                            className="nk-ibx-item-elem nk-ibx-item-fluid"
                            style={{ fontSize: " 0.875rem" }}
                          >
                            <div className="nk-ibx-context-group">
                              <div className="nk-ibx-context-badges">
                                <Badge
                                  color={
                                    moduleColors[item.moduleName] ||
                                    moduleColors.Default
                                  }
                                >
                                  {item.moduleName}
                                </Badge>
                              </div>
                              <div className="nk-ibx-context">
                                <span className="nk-ibx-context-text">
                                  <span className="heading">
                                    {item.messageTitle}
                                  </span>{" "}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="nk-ibx-item-elem nk-ibx-item-time">
                            <div
                              className="sub-text"
                              style={{ whiteSpace: "nowrap" }}
                            >
                              {item.createdAt &&
                                dayjs(item.createdAt).format(
                                  "DD-MM-YYYY HH:mm"
                                )}
                            </div>
                          </div>
                          <div className="nk-ibx-item-elem nk-ibx-item-tools">
                            <div className="ibx-actions">
                              <ul className="ibx-actions-visible gx-2">
                                <li>
                                  <UncontrolledDropdown>
                                    <DropdownToggle
                                      tag={"a"}
                                      href="#item"
                                      onClick={(ev) => ev.preventDefault()}
                                      className="dropdown-toggle btn btn-sm btn-icon btn-trigger"
                                    >
                                      <Icon name="more-h"></Icon>
                                    </DropdownToggle>
                                    <DropdownMenu end>
                                      <ul className="link-list-opt no-bdr">
                                        <li>
                                          <DropdownItem
                                            tag="a"
                                            href="#item"
                                            onClick={(ev) => {
                                              ev.preventDefault();
                                              onListClick(ev, item);
                                            }}
                                          >
                                            <Icon name="eye"></Icon>
                                            <span>View</span>
                                          </DropdownItem>
                                        </li>
                                        <li>
                                          <DropdownItem
                                            tag="a"
                                            href="#item"
                                            onClick={(ev) => {
                                              ev.preventDefault();
                                              handleDeleteSingle(
                                                item.notificationId
                                              );
                                            }}
                                          >
                                            <Icon name="trash"></Icon>
                                            <span>Delete</span>
                                          </DropdownItem>
                                        </li>
                                      </ul>
                                    </DropdownMenu>
                                  </UncontrolledDropdown>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      No Data
                    </div>
                  )}
                </SimpleBar>
              </React.Fragment>
            </React.Fragment>
          )}
        </>
      )}
    </div>
  );
};

export default NotificationList;
